import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Product } from '../types';
import api from '../utils/api';
import { 
  ArrowRight, 
  Star, 
  ShoppingBag, 
  Truck, 
  Shield, 
  RotateCcw,
  Sparkles,
  TrendingUp,
  Award,
  Zap,
  Users
} from 'lucide-react';
import PriceDisplay from '../components/PriceDisplay';

const Home: React.FC = () => {
  const [featuredProducts, setFeaturedProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchFeaturedProducts = async () => {
      try {
        const response = await api.get('/products?featured=true&limit=8');
        setFeaturedProducts(response.data.products);
      } catch (error) {
        console.error('Error fetching featured products:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFeaturedProducts();
  }, []);

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`h-4 w-4 ${
          i < Math.floor(rating) ? 'text-yellow-400 fill-current' : 'text-gray-300'
        }`}
      />
    ));
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-600 via-primary-700 to-purple-700 dark:from-primary-700 dark:via-primary-800 dark:to-purple-800 text-white overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0">
          <div className="absolute top-10 left-10 w-20 h-20 bg-white/10 rounded-full animate-pulse"></div>
          <div className="absolute top-32 right-20 w-16 h-16 bg-yellow-300/20 rounded-full animate-bounce delay-300"></div>
          <div className="absolute bottom-20 left-32 w-12 h-12 bg-pink-300/20 rounded-full animate-pulse delay-700"></div>
          <div className="absolute bottom-32 right-16 w-24 h-24 bg-blue-300/10 rounded-full animate-bounce delay-500"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 md:py-16 lg:py-20">
          <div className="text-center">
            {/* Badge */}
            <div className="inline-flex items-center bg-white/20 backdrop-blur-sm rounded-full px-4 py-2 mb-6 border border-white/30">
              <Sparkles className="w-4 h-4 mr-2 text-yellow-300" />
              <span className="text-sm font-medium text-white">India's Trusted Online Store</span>
              <div className="ml-2 w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
            </div>

            {/* Main heading - more compact for mobile */}
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 leading-tight">
              <span className="bg-gradient-to-r from-yellow-300 via-pink-300 to-yellow-300 bg-clip-text text-transparent">
                Amazing Products
              </span>
              <span className="block text-xl sm:text-2xl md:text-3xl lg:text-4xl mt-1">at Unbeatable Prices</span>
            </h1>

            <p className="text-base sm:text-lg md:text-xl mb-6 text-primary-100 dark:text-primary-200 max-w-3xl mx-auto px-4 leading-relaxed">
              India's fastest-growing marketplace
              <span className="hidden sm:block mt-1 font-medium">
                <span className="inline-flex items-center">
                  <Users className="w-4 h-4 mr-2" />
                  10,000+ Happy Customers
                </span>
                <span className="mx-3">•</span>
                <span className="inline-flex items-center">
                  <Award className="w-4 h-4 mr-2" />
                  Premium Quality
                </span>
              </span>
            </p>

            {/* CTA Buttons - more compact */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center items-center mb-6">
              <Link
                to="/products"
                className="group inline-flex items-center bg-white text-primary-600 px-6 py-3 rounded-xl text-base font-bold hover:bg-gray-50 transition-all duration-300 shadow-xl hover:shadow-2xl w-full sm:w-auto justify-center"
              >
                <ShoppingBag className="mr-2 h-5 w-5 group-hover:scale-110 transition-transform" />
                Start Shopping
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              
              <Link
                to="/products?featured=true"
                className="inline-flex items-center bg-transparent border-2 border-white text-white px-6 py-3 rounded-xl text-base font-semibold hover:bg-white hover:text-primary-600 transition-all duration-300 backdrop-blur-sm w-full sm:w-auto justify-center"
              >
                <TrendingUp className="mr-2 h-5 w-5" />
                Featured Products
              </Link>
            </div>

            {/* Trust indicators */}
            <div className="flex flex-wrap justify-center items-center gap-4 sm:gap-6 text-primary-100 opacity-90 pb-4 sm:pb-0">
              <div className="flex items-center">
                <Truck className="w-5 h-5 mr-2" />
                <span className="text-sm font-medium">Free Shipping</span>
              </div>
              <div className="flex items-center">
                <Shield className="w-5 h-5 mr-2" />
                <span className="text-sm font-medium">Secure Payment</span>
              </div>
              <div className="flex items-center">
                <RotateCcw className="w-5 h-5 mr-2" />
                <span className="text-sm font-medium">Easy Returns</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom wave */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" className="w-full h-16 sm:h-20">
            <path
              d="M0,64L48,69.3C96,75,192,85,288,80C384,75,480,53,576,48C672,43,768,53,864,64C960,75,1056,85,1152,80C1248,75,1344,53,1392,42.7L1440,32L1440,120L1392,120C1344,120,1248,120,1152,120C1056,120,960,120,864,120C768,120,672,120,576,120C480,120,384,120,288,120C192,120,96,120,48,120L0,120Z"
              fill="currentColor"
              className="text-white dark:text-secondary-900"
            />
          </svg>
        </div>
      </section>

      {/* Featured Products Section - more compact */}
      <section className="py-8 sm:py-12 bg-white dark:bg-secondary-900 transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-6 sm:mb-8">
            <div className="inline-flex items-center bg-gradient-to-r from-primary-100 to-purple-100 dark:from-primary-900/50 dark:to-purple-900/50 rounded-full px-4 py-2 mb-4">
              <Zap className="w-4 h-4 mr-2 text-primary-600 dark:text-primary-400" />
              <span className="text-xs font-bold text-primary-700 dark:text-primary-300 uppercase tracking-wide">
                Trending Now
              </span>
            </div>
            
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 dark:text-gray-100 mb-3">
              <span className="bg-gradient-to-r from-primary-600 to-purple-600 bg-clip-text text-transparent">
                Featured Products
              </span>
            </h2>
            
            <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 max-w-2xl mx-auto px-4">
              Handpicked bestsellers loved by thousands of customers
            </p>
          </div>

          {isLoading ? (
            <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
              {Array.from({ length: 8 }, (_, i) => (
                <div key={i} className="bg-white dark:bg-secondary-800 rounded-lg shadow-md p-4 animate-pulse">
                  <div className="bg-gray-300 dark:bg-secondary-700 h-40 sm:h-48 rounded-lg mb-4"></div>
                  <div className="bg-gray-300 dark:bg-secondary-700 h-4 rounded mb-2"></div>
                  <div className="bg-gray-300 dark:bg-secondary-700 h-4 rounded mb-2 w-3/4"></div>
                  <div className="bg-gray-300 dark:bg-secondary-700 h-6 rounded w-1/2"></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
              {featuredProducts.slice(0, 8).map((product) => (
                <Link
                  key={product._id}
                  to={`/products/${product._id}`}
                  className="group bg-white dark:bg-secondary-800 rounded-xl shadow-md hover:shadow-xl dark:hover:shadow-xl transition-all duration-300 p-3 sm:p-4 border border-gray-100 dark:border-secondary-700 hover:border-primary-200 dark:hover:border-primary-700 transform hover:-translate-y-1"
                >
                  <div className="relative mb-3 overflow-hidden rounded-lg bg-white dark:bg-gray-50 p-3 flex items-center justify-center">
                    <img
                      src={product.images[0]}
                      alt={product.name}
                      className="w-full h-32 sm:h-40 object-contain group-hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                    />
                    
                    {/* Stock badge - simplified for mobile */}
                    <div className="absolute top-2 left-2">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-bold ${
                        product.stock > 0 
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300' 
                          : 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300'
                      }`}>
                        {product.stock > 0 ? '✓' : '✕'}
                      </span>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <h3 className="text-sm sm:text-base font-bold text-gray-900 dark:text-gray-100 line-clamp-2 min-h-[2.5rem] group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                      {product.name}
                    </h3>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-1">
                        <div className="flex">
                          {renderStars(product.ratings.average)}
                        </div>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          ({product.ratings.count})
                        </span>
                      </div>
                      
                      {product.ratings.average >= 4 && (
                        <div className="flex items-center bg-yellow-100 dark:bg-yellow-900/50 rounded-full px-1.5 py-0.5">
                          <Award className="w-2.5 h-2.5 text-yellow-600 dark:text-yellow-400 mr-1" />
                          <span className="text-xs font-bold text-yellow-700 dark:text-yellow-300">Top</span>
                        </div>
                      )}
                    </div>

                    <PriceDisplay
                      price={product.price}
                      originalPrice={product.originalPrice}
                      discountedPrice={product.discountedPrice}
                      className="text-base sm:text-lg font-bold"
                      showDiscountPercentage={true}
                    />
                  </div>
                </Link>
              ))}
            </div>
          )}

          <div className="text-center mt-6 sm:mt-8">
            <Link
              to="/products"
              className="group inline-flex items-center bg-gradient-to-r from-primary-600 to-purple-600 hover:from-primary-700 hover:to-purple-700 text-white px-6 py-3 rounded-xl text-base font-bold transition-all duration-300 shadow-lg hover:shadow-xl w-full sm:w-auto justify-center"
            >
              <span className="mr-3">View All Products</span>
              <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="relative bg-gradient-to-br from-gray-50 to-primary-50 dark:from-secondary-800 dark:to-secondary-700 py-16 sm:py-20 transition-colors overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-32 h-32 bg-primary-200/30 dark:bg-primary-800/30 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-40 h-40 bg-purple-200/30 dark:bg-purple-800/30 rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <div className="inline-flex items-center bg-white dark:bg-secondary-800 rounded-full px-6 py-3 mb-6 shadow-lg">
              <Sparkles className="w-5 h-5 mr-2 text-primary-600 dark:text-primary-400" />
              <span className="text-sm font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                Why Choose Us
              </span>
            </div>
            
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-gray-100 mb-6">
              Experience the
              <span className="block bg-gradient-to-r from-primary-600 to-purple-600 bg-clip-text text-transparent">
                E-Nest Difference
              </span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Free Shipping */}
            <div className="group bg-white dark:bg-secondary-800 rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100 dark:border-secondary-700">
              <div className="relative mb-6">
                <div className="w-16 h-16 bg-gradient-to-r from-green-400 to-emerald-500 rounded-2xl flex items-center justify-center mx-auto group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <Truck className="w-8 h-8 text-white" />
                </div>
                <div className="absolute -top-2 -right-2 w-6 h-6 bg-green-100 dark:bg-green-900/50 rounded-full flex items-center justify-center">
                  <span className="text-xs font-bold text-green-600 dark:text-green-400">✓</span>
                </div>
              </div>
              
              <h3 className="text-xl font-bold mb-3 text-gray-900 dark:text-gray-100 text-center">
                Free Shipping
              </h3>
              <p className="text-gray-600 dark:text-gray-400 text-center leading-relaxed">
                <span className="font-semibold text-green-600 dark:text-green-400">Free delivery</span> on orders above ₹999 
                <span className="block mt-1 text-sm">Across India</span>
              </p>
            </div>

            {/* Secure Payment */}
            <div className="group bg-white dark:bg-secondary-800 rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100 dark:border-secondary-700">
              <div className="relative mb-6">
                <div className="w-16 h-16 bg-gradient-to-r from-blue-400 to-indigo-500 rounded-2xl flex items-center justify-center mx-auto group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <Shield className="w-8 h-8 text-white" />
                </div>
                <div className="absolute -top-2 -right-2 w-6 h-6 bg-blue-100 dark:bg-blue-900/50 rounded-full flex items-center justify-center">
                  <span className="text-xs font-bold text-blue-600 dark:text-blue-400">🔒</span>
                </div>
              </div>
              
              <h3 className="text-xl font-bold mb-3 text-gray-900 dark:text-gray-100 text-center">
                Secure Payment
              </h3>
              <p className="text-gray-600 dark:text-gray-400 text-center leading-relaxed">
                <span className="font-semibold text-blue-600 dark:text-blue-400">100% safe</span> & secure payments
                <span className="block mt-1 text-sm">SSL encrypted</span>
              </p>
            </div>

            {/* Easy Returns */}
            <div className="group bg-white dark:bg-secondary-800 rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100 dark:border-secondary-700">
              <div className="relative mb-6">
                <div className="w-16 h-16 bg-gradient-to-r from-orange-400 to-red-500 rounded-2xl flex items-center justify-center mx-auto group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <RotateCcw className="w-8 h-8 text-white" />
                </div>
                <div className="absolute -top-2 -right-2 w-6 h-6 bg-orange-100 dark:bg-orange-900/50 rounded-full flex items-center justify-center">
                  <span className="text-xs font-bold text-orange-600 dark:text-orange-400">↻</span>
                </div>
              </div>
              
              <h3 className="text-xl font-bold mb-3 text-gray-900 dark:text-gray-100 text-center">
                Easy Returns
              </h3>
              <p className="text-gray-600 dark:text-gray-400 text-center leading-relaxed">
                <span className="font-semibold text-orange-600 dark:text-orange-400">Hassle-free</span> returns & exchanges
                <span className="block mt-1 text-sm">30-day policy</span>
              </p>
            </div>
          </div>

          {/* Stats Section */}
          <div className="mt-16 sm:mt-20">
            <div className="bg-white dark:bg-secondary-800 rounded-3xl p-8 sm:p-12 shadow-2xl border border-gray-100 dark:border-secondary-700">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
                <div className="text-center">
                  <div className="text-3xl sm:text-4xl font-bold text-primary-600 dark:text-primary-400 mb-2">10K+</div>
                  <div className="text-sm font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">Happy Customers</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl sm:text-4xl font-bold text-blue-600 dark:text-blue-400 mb-2">99.9%</div>
                  <div className="text-sm font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">Uptime</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl sm:text-4xl font-bold text-purple-600 dark:text-purple-400 mb-2">4.8★</div>
                  <div className="text-sm font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">Rating</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;